import React from 'react';
import { reportClientError } from '../services/api';

// Red de seguridad: si cualquier pantalla crashea al renderizar, en vez de
// quedar en blanco mostramos un cartel con un botón de recargar y el detalle
// del error para que el cliente lo pueda copiar/mandar. Los Error Boundary
// tienen que ser clases (React no permite hacerlo con hooks).
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Queda en la consola del navegador para diagnóstico (F12 → Console).
    // eslint-disable-next-line no-console
    console.error('[BarberApp] Error de render:', error, info);
    this.setState({ info });
    // Y al servidor, para verlo en el monitoreo del panel admin aunque el
    // cliente no nos mande la captura.
    reportClientError({
      message: (error && (error.message || String(error))) || 'Error desconocido',
      stack: (error && error.stack) || '',
      componentStack: (info && info.componentStack) || '',
      url: typeof window !== 'undefined' ? window.location.href : '',
      language: typeof navigator !== 'undefined' ? navigator.language : '',
      screen:
        typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, info } = this.state;
    const details = [
      error && (error.stack || error.message || String(error)),
      info && info.componentStack,
      typeof navigator !== 'undefined' ? `Navegador: ${navigator.userAgent}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');

    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={styles.emoji}>😵‍💫</div>
          <h1 style={styles.title}>Algo salió mal</h1>
          <p style={styles.text}>
            Tuvimos un problema al mostrar esta pantalla. Probá recargar la
            página. Si vuelve a pasar, mandanos la captura del detalle de abajo.
          </p>
          <button type="button" style={styles.btn} onClick={this.handleReload}>
            Recargar la página
          </button>
          {details ? (
            <details style={styles.details}>
              <summary style={styles.summary}>Ver detalle del error</summary>
              <pre style={styles.pre}>{details}</pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}

// Estilos inline a propósito: así el cartel funciona aunque falle una hoja de
// estilos. Tonos oscuros para que combine con el panel.
const styles = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: '#0b0f1a',
    boxSizing: 'border-box',
    fontFamily:
      "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  card: {
    maxWidth: 440,
    width: '100%',
    background: '#111827',
    border: '1px solid #1f2937',
    borderRadius: 18,
    padding: 28,
    textAlign: 'center',
    color: '#e5e7eb',
    boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
  },
  emoji: { fontSize: 40, marginBottom: 8 },
  title: { margin: '0 0 8px', fontSize: 22, fontWeight: 700 },
  text: { margin: '0 0 20px', fontSize: 15, lineHeight: 1.5, color: '#9ca3af' },
  btn: {
    appearance: 'none',
    border: 'none',
    cursor: 'pointer',
    background: '#ec4899',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    padding: '12px 20px',
    borderRadius: 12,
    width: '100%',
  },
  details: { marginTop: 18, textAlign: 'left' },
  summary: {
    cursor: 'pointer',
    fontSize: 13,
    color: '#9ca3af',
    userSelect: 'none',
  },
  pre: {
    marginTop: 10,
    maxHeight: 220,
    overflow: 'auto',
    background: '#0b0f1a',
    border: '1px solid #1f2937',
    borderRadius: 10,
    padding: 12,
    fontSize: 11,
    lineHeight: 1.45,
    color: '#cbd5e1',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
};

export default ErrorBoundary;
